// src/pages/DealersStockView/hooks/useDealerPartsData.js
import { useCallback, useMemo, useState } from 'react';
import { useGetDealersStockByDealerIdQuery } from '../../../api';

const useDealerPartsData = (isDealer = false) => {
    const [selection, setSelection] = useState(null);
    const [pagination, setPagination] = useState({ PageNumber: 1, PageSize: 10 });
    const [partCodeFilter, setPartCodeFilter] = useState('');
    const [partDescFilter, setPartDescFilter] = useState('');
    const [blockFilter, setBlockFilter] = useState('');
    const [shelfFilter, setShelfFilter] = useState('');
    const [sequenceFilter, setSequenceFilter] = useState('');
    const [eyesFilter, setEyesFilter] = useState('');

    const dealerId = selection?.dealerId;
    const dealerAddressId = selection?.dealerAddressId;

    const queryParams = isDealer
        ? {
              PageNumber: pagination.PageNumber,
              PageSize: pagination.PageSize,
              ...(partCodeFilter ? { PartCode: partCodeFilter } : {}),
              ...(partDescFilter ? { PartDesc: partDescFilter } : {}),
              ...(blockFilter ? { Block: blockFilter } : {}),
              ...(shelfFilter ? { Shelf: shelfFilter } : {}),
              ...(sequenceFilter ? { Sequence: sequenceFilter } : {}),
              ...(eyesFilter ? { Eyes: eyesFilter } : {})
          }
        : {
              DealerId: dealerId,
              DealerAddressId: dealerAddressId,
              PageNumber: pagination.PageNumber,
              PageSize: pagination.PageSize,
              ...(partCodeFilter ? { PartCode: partCodeFilter } : {}),
              ...(partDescFilter ? { PartDesc: partDescFilter } : {}),
              ...(blockFilter ? { Block: blockFilter } : {}),
              ...(shelfFilter ? { Shelf: shelfFilter } : {}),
              ...(sequenceFilter ? { Sequence: sequenceFilter } : {}),
              ...(eyesFilter ? { Eyes: eyesFilter } : {})
          };

    const shouldSkip = isDealer ? false : !dealerId || !dealerAddressId;

    const { data, isLoading, isFetching, refetch } = useGetDealersStockByDealerIdQuery(queryParams, { skip: shouldSkip });

    const selectAddress = useCallback((payload) => {
        setSelection(payload);
        setPagination({ PageNumber: 1, PageSize: 10 });
    }, []);

    const clearSelection = useCallback(() => setSelection(null), []);

    const onPageChange = useCallback((event) => {
        const newPageNumber = Math.floor(event.first / event.rows) + 1;
        setPagination({ PageNumber: newPageNumber, PageSize: event.rows });
    }, []);

    const applyPartCodeFilter = useCallback((partCode) => {
        setPartCodeFilter(partCode || '');
        setPagination(prev => ({ ...prev, PageNumber: 1 }));
    }, []);

    const applyPartDescFilter = useCallback((partDesc) => {
        setPartDescFilter(partDesc || '');
        setPagination(prev => ({ ...prev, PageNumber: 1 }));
    }, []);

    const applyFilters = useCallback((partCode, partDesc, block, shelf, sequence, eyes) => {
        setPartCodeFilter(partCode || '');
        setPartDescFilter(partDesc || '');
        setBlockFilter(block || '');
        setShelfFilter(shelf || '');
        setSequenceFilter(sequence || '');
        setEyesFilter(eyes || '');
        setPagination(prev => ({ ...prev, PageNumber: 1 }));
    }, []);

    const rows = data?.rows ?? data?.Rows ?? [];
    const totalCount = data?.totalCount ?? data?.TotalCount ?? 0;
    const loading = isLoading || isFetching;

    return {
        selection,
        selectAddress,
        clearSelection,
        rows,
        totalCount,
        loading,
        pagination,
        onPageChange,
        refetch,
        partCodeFilter,
        applyPartCodeFilter,
        partDescFilter,
        applyPartDescFilter,
        blockFilter,
        shelfFilter,
        sequenceFilter,
        eyesFilter,
        applyFilters
    };
};

export default useDealerPartsData;
